/**
 * Permission checker utility for role-based access control
 */

/**
 * Checks if a user has permission based on their role
 * @param {string} userRole - The user's role (from userProfile.role)
 * @param {string[]} allowedRoles - Array of roles that have permission
 * @returns {boolean} - True if user has permission, false otherwise
 */
export const hasPermission = (userRole, allowedRoles) => {
    if (!userRole) return false;
    if (!allowedRoles || allowedRoles.length === 0) return true;

    return allowedRoles.includes(userRole);
};

/**
 * Checks if a user has permission based on their role
 * @param {string} userRole - The user's role (from userProfile.role)
 * @param {string[]} allowedRoles - Array of roles that have permission
 * @returns {boolean} - True if user has permission, false otherwise
 */
export const checkPermissions = (userPermissions, requiredPermissions) => {

    // No user permissions
    if (!Array.isArray(userPermissions) || userPermissions.length === 0) {
        return false;
    }

    // If nothing is required → allow
    if (!Array.isArray(requiredPermissions) || requiredPermissions.length === 0) {
        return true;
    }


    // Check if user has all the required permission
    return requiredPermissions.every(permission =>
        userPermissions.includes(permission)
    );

};

/**
 * React hook for checking permissions
 * @param {object} userProfile - The user profile object
 * @param {string[]} allowedRoles - Array of roles that have permission
 * @returns {boolean} - True if user has permission, false otherwise
 */
export const usePermission = (userProfile, allowedRoles) => {
    return hasPermission(userProfile?.role, allowedRoles);
};

/**
 * Higher-order component for protecting components with role-based permissions
 * @param {React.Component} Component - The component to protect
 * @param {string[]} allowedRoles - Array of roles that have permission
 * @param {React.Component} fallback - Optional fallback component to render if no permission
 * @returns {React.Component} - Protected component
 */
export const withPermission = (Component, allowedRoles, fallback = null) => {
    return (props) => {
        const { userProfile } = props;

        if (hasPermission(userProfile?.role, allowedRoles)) {
            return <Component {...props} />;
        }

        return fallback;
    };
};
